import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { useAuthStore } from '@/store/authStore'

export default function WeChatBindingPage() {
  const { user } = useAuthStore()
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null)
  const [bindingStatus, setBindingStatus] = useState<{
    isBound: boolean
    bindingInfo: {
      id: string
      nickname: string | null
      avatarUrl: string | null
      boundAt: string
      isActive: boolean
    } | null
  } | null>(null)
  const [pushConfig, setPushConfig] = useState<{
    id: string
    pushTime: string
    isEnabled: boolean
    pushDays: number[]
  } | null>(null)

  // Fetch binding status
  const { data: bindingData, refetch: refetchBinding } = trpc.wechat.getBindingStatus.useQuery(
    { userId: user?.id || 0 },
    { enabled: !!user?.id }
  )

  // Fetch push config
  const { data: pushData, refetch: refetchPush } = trpc.wechat.getPushConfig.useQuery(
    { userId: user?.id || 0 },
    { enabled: !!user?.id }
  )

  // Generate QR code mutation
  const generateQRCode = trpc.wechat.generateBindingQRCode.useMutation({
    onSuccess: (data) => {
      setQrCodeUrl(data.url)
    },
    onError: (error) => {
      toast.error('生成二维码失败', { description: error.message })
    },
  })

  // Unbind mutation
  const unbind = trpc.wechat.unbindWeChat.useMutation({
    onSuccess: (data) => {
      if (data.success) {
        toast.success('解绑成功', { description: '微信账号已解绑' })
        refetchBinding()
        refetchPush()
      } else {
        toast.error('解绑失败', { description: data.error })
      }
    },
    onError: (error) => {
      toast.error('解绑失败', { description: error.message })
    },
  })

  // Update push config mutation
  const updatePushConfig = trpc.wechat.updatePushConfig.useMutation({
    onSuccess: (data) => {
      if (data.success) {
        toast.success('更新成功', { description: '推送配置已更新' })
        refetchPush()
      } else {
        toast.error('更新失败', { description: data.error })
      }
    },
    onError: (error) => {
      toast.error('更新失败', { description: error.message })
    },
  })

  // Send test push mutation
  const sendTestPush = trpc.wechat.sendTestPush.useMutation({
    onSuccess: (data) => {
      if (data.success) {
        toast.success('测试消息已发送', { description: '请查看微信消息' })
      } else {
        toast.error('发送失败', { description: data.error })
      }
    },
    onError: (error) => {
      toast.error('发送失败', { description: error.message })
    },
  })

  useEffect(() => {
    if (bindingData) {
      setBindingStatus(bindingData)
    }
  }, [bindingData])

  useEffect(() => {
    if (pushData) {
      setPushConfig(pushData)
    }
  }, [pushData])

  const handleGenerateQRCode = () => {
    if (user?.id) {
      generateQRCode.mutate({ userId: user.id })
    }
  }

  const handleUnbind = () => {
    if (user?.id && confirm('确定要解绑微信账号吗？解绑后将无法收到推送消息。')) {
      unbind.mutate({ userId: user.id })
    }
  }

  const handleTogglePush = (enabled: boolean) => {
    if (user?.id) {
      updatePushConfig.mutate({
        userId: user.id,
        isEnabled: enabled,
      })
    }
  }

  const handleUpdatePushTime = (time: string) => {
    if (user?.id && /^\d{2}:\d{2}$/.test(time)) {
      updatePushConfig.mutate({
        userId: user.id,
        pushTime: time,
      })
    }
  }

  const handleTogglePushDay = (day: number) => {
    if (user?.id && pushConfig) {
      const newDays = pushConfig.pushDays.includes(day)
        ? pushConfig.pushDays.filter(d => d !== day)
        : [...pushConfig.pushDays, day].sort()

      updatePushConfig.mutate({
        userId: user.id,
        pushDays: newDays,
      })
    }
  }

  const handleSendTestPush = () => {
    if (user?.id) {
      sendTestPush.mutate({ userId: user.id })
    }
  }

  const dayNames = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']

  return (
    <div className="container mx-auto py-8 space-y-6">
      <h1 className="text-3xl font-bold">微信绑定</h1>

      {/* Binding Status Card */}
      <Card>
        <CardHeader>
          <CardTitle>绑定状态</CardTitle>
          <CardDescription>
            绑定微信账号后，可以收到每日学习任务提醒
          </CardDescription>
        </CardHeader>
        <CardContent>
          {bindingStatus?.isBound ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                {bindingStatus.bindingInfo?.avatarUrl && (
                  <img
                    src={bindingStatus.bindingInfo.avatarUrl}
                    alt="微信头像"
                    className="w-12 h-12 rounded-full"
                  />
                )}
                <div>
                  <p className="font-medium">
                    {bindingStatus.bindingInfo?.nickname || '微信用户'}
                  </p>
                  <p className="text-sm text-gray-500">
                    绑定时间：{new Date(bindingStatus.bindingInfo?.boundAt || '').toLocaleDateString()}
                  </p>
                </div>
                <Badge variant="success">已绑定</Badge>
              </div>
              <Button variant="destructive" onClick={handleUnbind}>
                解绑微信
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-gray-600">
                扫描下方二维码关注公众号，即可完成绑定
              </p>
              {qrCodeUrl ? (
                <div className="flex flex-col items-center space-y-4">
                  <img src={qrCodeUrl} alt="绑定二维码" className="w-64 h-64" />
                  <p className="text-sm text-gray-500">
                    二维码有效期：7天
                  </p>
                </div>
              ) : (
                <Button onClick={handleGenerateQRCode} disabled={generateQRCode.isLoading}>
                  {generateQRCode.isLoading ? '生成中...' : '生成绑定二维码'}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Push Configuration Card */}
      {bindingStatus?.isBound && pushConfig && (
        <Card>
          <CardHeader>
            <CardTitle>推送设置</CardTitle>
            <CardDescription>
              配置每日任务提醒的时间和频率
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable/Disable Push */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>启用推送</Label>
                <p className="text-sm text-gray-500">
                  开启后将收到每日任务提醒
                </p>
              </div>
              <Switch
                checked={pushConfig.isEnabled}
                onCheckedChange={handleTogglePush}
              />
            </div>

            {/* Push Time */}
            <div className="space-y-2">
              <Label>推送时间</Label>
              <Input
                type="time"
                value={pushConfig.pushTime}
                onChange={(e) => handleUpdatePushTime(e.target.value)}
                className="w-32"
              />
              <p className="text-sm text-gray-500">
                设置每天发送提醒的时间
              </p>
            </div>

            {/* Push Days */}
            <div className="space-y-2">
              <Label>推送日期</Label>
              <div className="flex flex-wrap gap-2">
                {dayNames.map((name, index) => (
                  <Button
                    key={index}
                    variant={pushConfig.pushDays.includes(index) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleTogglePushDay(index)}
                  >
                    {name}
                  </Button>
                ))}
              </div>
              <p className="text-sm text-gray-500">
                选择需要接收提醒的日期
              </p>
            </div>

            {/* Test Push */}
            <div className="space-y-2">
              <Label>测试推送</Label>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={handleSendTestPush}
                  disabled={sendTestPush.isLoading || !pushConfig.isEnabled}
                >
                  {sendTestPush.isLoading ? '发送中...' : '发送测试消息'}
                </Button>
              </div>
              <p className="text-sm text-gray-500">
                发送一条测试消息到微信，验证推送功能
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
